/**
 * The main front-end entry point for the calendar app.
 */

import '../css/index.css';
import { render } from 'preact';
import * as cal from './cal/routes';
import * as courses from './courses/routes';
import { Toaster } from '@components/toaster';
import { createHistoryRouter, AppRouter, RouteRule, RouterPage } from '@components/router';
import { GlobalComponents } from '@components/dialog';
import { AuthContextProvider } from './lib/auth';
import { CoursesApp } from './courses';

import { RuzukuGlobal } from 'server/types';
import { initClockOffset, serverNow } from 'client/lib/clock';
import { setNowFn } from 'shared/meeting-utils';
import { captureSignupAttribution } from 'client/lib/signup-attribution';
import { initSentry } from 'client/lib/sentry';
import {
  handleLazyLoadError,
  listenForPreloadErrors,
  reportPendingChunkReload,
} from 'client/lib/chunk-errors';

declare global {
  interface Window {
    ruzukuGlobal?: RuzukuGlobal;
  }
}

/**
 * Render the root component, and wire up the application context.
 */
function App(props: { router: AppRouter }) {
  return (
    <AuthContextProvider>
      <RouterPage router={props.router} after={<GlobalComponents />}>
        <CoursesApp />
      </RouterPage>
      <Toaster />
    </AuthContextProvider>
  );
}

function loadCalendarLandingPageApp() {
  return import('./components/cal-landing-page-app');
}

function addRoutes(router: AppRouter, app: { routes: RouteRule[] }) {
  app.routes.forEach(([loadModule, ...patterns]) =>
    patterns.forEach((url) => router.add({ loadModule, url })),
  );
}

/**
 * Mount the application.
 */
function mountApp() {
  // Start Sentry before anything else loads, so failures loading route
  // chunks and the calendar landing page are reported too.
  if (window.ruzukuGlobal) {
    initSentry(window.ruzukuGlobal);
  }
  reportPendingChunkReload();
  listenForPreloadErrors();

  // Sync meeting time checks with the server clock to handle
  // students with incorrect system clocks (e.g. Arizona DST). See #6786.
  if (window.ruzukuGlobal?.serverTime) {
    initClockOffset(window.ruzukuGlobal.serverTime);
    setNowFn(serverNow);
  }

  const mainElement = document.querySelector('main');
  if (!mainElement) {
    console.error('Main element not found - cannot mount application');
    return;
  }

  // Check if we should render the calendar landing page instead
  if (window.ruzukuGlobal?.app === 'cal-landing-page') {
    loadCalendarLandingPageApp()
      .then(({ CalendarLandingPageApp }) => {
        render(<CalendarLandingPageApp />, mainElement);
      })
      .catch((err) => {
        handleLazyLoadError(err, 'calendar-landing-page');
      });
    return;
  }

  captureSignupAttribution();
  // Render the main app
  const router: AppRouter = createHistoryRouter();
  addRoutes(router, cal);
  addRoutes(router, courses);
  render(<App router={router} />, mainElement);
}

mountApp();
